// Metric.tsx — one number as a card in its own right, for the rows of figures
// on the status and cost routes.
//
// The shape is the estate's answer to "one number, read at a glance", already
// shipped by inference's GPU panel and imprinter's progress page: a tinted icon
// beside the label in the header, the figure centered in the body at display
// size, and the caption that qualifies it directly underneath. Header and body
// only — the caption belongs under the figure it explains, and a footer would
// put a rule between the number and the words about it, which leaves the
// footer with nothing to say (DESIGN.md C-CARD-1: a slot is for content, not
// for symmetry).
import type { ReactNode } from 'react';
import Card from 'react-bootstrap/Card';

export default function Metric({ icon, label, value, note, badge }: {
  icon: ReactNode;
  label: string;
  value: string;
  note: ReactNode;
  // Anything qualifying the *state* of the figure rather than its size — the
  // overflow chip on the queue card is the case this exists for. It sits in
  // the header, opposite the label, where a badge reads as a status and not as
  // part of the number.
  badge?: ReactNode;
}) {
  return (
    // h-100 so a row of these keeps a flat top and bottom edge: captions run to
    // one or two lines depending on the number, and the figures only line up
    // across the row if every card is the height of the tallest.
    <Card className="h-100">
      <Card.Header className="d-flex align-items-center justify-content-between gap-2">
        <span className="d-flex align-items-center gap-2">
          {icon}
          <span className="small">{label}</span>
        </span>
        {badge}
      </Card.Header>
      <Card.Body className="d-flex flex-column justify-content-center align-items-center text-center">
        <div className="display-6 fw-semibold lh-1">{value}</div>
        <div className="small text-muted mt-2">{note}</div>
      </Card.Body>
    </Card>
  );
}
